import React from 'react'
import { ChevronLeft } from 'lucide-react'
import { ApartmentFootprint, PanoramaNode } from '../types'

// Export sample nodes for reuse in other components
export const samplePanoramaNodes: PanoramaNode[] = [
  {
    id: 'living-room',
    x: 20,
    y: 55,
    label: 'Living Room',
    panoramaUrl: '/panoramas/panorama_2.png',
    initialRotation: { longitude: 0, latitude: 0 },
    hotspots: [
      {
        id: 'hotspot-lr-kitchen',
        targetPanoramaId: 'kitchen',
        targetRoomName: 'Kitchen',
        sphericalCoords: { theta: 220, phi: -10 }
      },
      {
        id: 'hotspot-lr-hallway',
        targetPanoramaId: 'hallway1',
        targetRoomName: 'Hallway',
        sphericalCoords: { theta: 170, phi: -10 }
      }
    ]
  },
  {
    id: 'kitchen',
    x: 35,
    y: 20,
    label: 'Kitchen',
    panoramaUrl: '/panoramas/panorama_1.png',
    initialRotation: { longitude: 200, latitude: 10 },
    hotspots: [
      {
        id: 'hotspot-k-living',
        targetPanoramaId: 'living-room',
        targetRoomName: 'Living Room',
        sphericalCoords: { theta: 40, phi: -10 }
      },
    ]
  },
  {
    id: 'bathroom',
    x: 62,
    y: 30,
    label: 'Bathroom',
    panoramaUrl: '/panoramas/panorama_5.png',
    initialRotation: { longitude: 180, latitude: 0 },
    hotspots: [
      {
        id: 'hotspot-b-hallway',
        targetPanoramaId: 'hallway1',
        targetRoomName: 'Hallway',
        sphericalCoords: { theta: 100, phi: -10 }
      }
    ]
  },
  {
    id: 'hallway1',
    x: 60,
    y: 70,
    label: 'Hallway',
    panoramaUrl: '/panoramas/panorama_3.png',
    initialRotation: { longitude: 180, latitude: 0 },
    hotspots: [
      {
        id: 'hotspot-h-bathroom',
        targetPanoramaId: 'bathroom',
        targetRoomName: 'Bathroom',
        sphericalCoords: { theta: 280, phi: -10 }
      },
      {
        id: 'hotspot-h-living',
        targetPanoramaId: 'living-room',
        targetRoomName: 'Living Room',
        sphericalCoords: { theta: 0, phi: -10 }
      },
      {
        id: 'hotspot-h-entrance',
        targetPanoramaId: 'entrance',
        targetRoomName: 'Entrance',
        sphericalCoords: { theta: 180, phi: -10 }
      }
    ]
  },
  {
    id: 'entrance',
    x: 85,
    y: 70,
    label: 'Entrance',
    panoramaUrl: '/panoramas/panorama_4.png',
    initialRotation: { longitude: 90, latitude: 0 },
    hotspots: [
      {
        id: 'hotspot-e-hallway',
        targetPanoramaId: 'hallway1',
        targetRoomName: 'Hallway',
        sphericalCoords: { theta: 0, phi: -10 }
      }
    ]
  }
]

export function ApartmentDetails ({
  footprint,
  onBack,
}: {
  footprint: ApartmentFootprint;
  onBack: () => void;
}): JSX.Element {
  const { id, name, area, floor, rooms, price, apt_plans } = footprint

  // Determine render image based on apartment ID
  const getRenderImagePath = (apartmentId: string): string => {
    // First two apartments get render_image_1
    if (['A2', 'B1'].includes(apartmentId)) {
      return '/render_images/render_image_1.webp'
    }
    // Next two apartments get render_image_2  
    if (['B3', 'B4'].includes(apartmentId)) {
      return '/render_images/render_image_2.webp'
    }
    // Next two apartments get render_image_3
    if (['C1', 'C2'].includes(apartmentId)) {
      return '/render_images/render_image_3.webp'
    }
    // Default to render_image_1 for other apartments
    return '/render_images/render_image_1.webp'
  }

  return (
    <div
      className="bg-[#ffffffa1] backdrop-blur-md rounded-2xl w-full h-full p-4 flex flex-col pointer-events-auto shadow-xl overflow-hidden">
      <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex gap-3 items-center">
          <button
            onClick={onBack}
            className="p-2 bg-white rounded-full hover:bg-gray-300 shadow-xl"
          >
            <ChevronLeft size={20}/>
          </button>
          <h2 className="text-2xl font-semibold">{name}</h2>
        </div>
        <div className="flex items-center space-x-1">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#0080ff' }}/>
          <span className="font-light">Available</span>
        </div>
      </div>

      {/* Price */}
      {price ? (
        <div className="mt-3">
          <p className="text-2xl font-bold">
            €{price.toLocaleString()}
          </p>
        </div>
        // <div className="flex items-center space-x-1">
        //   <h2 className="text-2xl font-semibold">{name}</h2>
        // </div>
      ) : (
        <div className="mt-3 text-center">
          <p className="text-sm text-red-500"></p>
        </div>
      )}

      {/* Info Grid */}
      <div className="mt-4 grid grid-cols-3 gap-4">
        {[
          ['Area', `${area.toFixed(2)}㎡`],
          ['Floor', floor],
          ['Rooms', rooms.length],
        ].map(([label, value]) => (
          <div
            key={label}
            className="bg-white shadow-xl p-2 rounded-xl text-center"
          >
            <p className="text-sm text-gray-500">{label}</p>
            <p className="font-medium">{value}</p>
          </div>
        ))}
      </div>

      {/* Apartment Plan Image */}
      {apt_plans && (
        <div className="mt-4">
          <h3 className="text-lg font-medium mb-2">Floor Plan</h3>
          <div className="bg-white rounded-xl shadow-xl p-2 h-[300px] overflow-hidden">
            <img
              src={apt_plans}
              alt={`Floor plan for ${name}`}
              className="w-full h-full object-contain rounded-lg bg-white"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
          
          {/* Render Image */}
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Render</h3>
            <div className="bg-white rounded-xl shadow-xl p-2 h-[300px] overflow-hidden">
              <img
                src={getRenderImagePath(id)}
                alt={`3D render for ${name}`}
                className="w-full h-full object-contain rounded-lg bg-white"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  )
}

